import Intercom, { shutdown, update } from '@intercom/messenger-js-sdk';
import { useEffect, useRef } from 'react';
import { create } from 'zustand';
import { config } from '@/config';
import { useCurrentUserContext } from '@/contexts/CurrentUserContext';
import { useIsMobile } from './useIsBreakpoint';

type Options = {
  hideOnMobile?: boolean;
};

type LauncherStore = {
  isLauncherHidden: boolean;
  setLauncherHidden: (isLauncherHidden: boolean) => void;
};

// The launcher parks itself in the bottom-right corner, which a surface like
// the planner's first-run guide needs for itself. Held in a store rather than
// an option so the surface that wants the corner can ask for it directly,
// without the page that happens to boot Intercom having to know about it.
const useLauncherStore = create<LauncherStore>((set) => ({
  isLauncherHidden: false,
  setLauncherHidden: (isLauncherHidden) => set({ isLauncherHidden }),
}));

/**
 * Stands the Intercom launcher down while a surface owns the bottom-right
 * corner. Restores it as soon as that stops being true, and on unmount, so a
 * guide that is finished, skipped, or navigated away from mid-run cannot leave
 * support unreachable.
 */
export const useHideIntercomLauncher = (isHidden: boolean) => {
  const setLauncherHidden = useLauncherStore(
    (state) => state.setLauncherHidden
  );

  useEffect(() => {
    if (!isHidden) return;

    setLauncherHidden(true);

    return () => setLauncherHidden(false);
  }, [isHidden, setLauncherHidden]);
};

export const useIntercom = (options?: Options) => {
  const { currentUser } = useCurrentUserContext();
  const { isMobile } = useIsMobile();
  const isLauncherHidden = useLauncherStore((state) => state.isLauncherHidden);

  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    if (isMobile && optionsRef.current?.hideOnMobile) {
      return;
    }
    if (config.intercom.appId) {
      Intercom({ app_id: config.intercom.appId });
      return () => shutdown();
    }
  }, [isMobile]);

  // set user attributes
  useEffect(() => {
    if (!config.intercom.appId) {
      return;
    }

    // Sent on every update rather than only while hiding: a launcher parked by
    // a guide that has since ended stays parked until something says otherwise.
    const props: Record<string, string | boolean | undefined> = {
      hide_default_launcher: isLauncherHidden,
    };

    if (currentUser) {
      props.user_id = currentUser.id;
      if (currentUser.intercomUserHash) {
        props.user_hash = currentUser.intercomUserHash;
      }
      if (currentUser.email) {
        props.email = currentUser.email;
      }
      if (currentUser.name) {
        props.name = currentUser.name;
      }
    }

    update(props);
  });
};
